/**
 * CompanyDashboard — Company hub v2.
 *
 * Source: design_handoff_tank manage-redesign-v2 brief.
 *
 * Layout:
 *   1. Pinned identity card at top (company name + slug + your role).
 *   2. Three groups of section cards:
 *      - :: Workspace  → Users
 *      - :: Output     → FTP Targets
 *      - :: Captions   → Profiles, Caption Builder, Settings
 *   3. Each card shows: letter avatar + name + desc + big phos count +
 *      inline preview of contents (first 3 entries with status dots
 *      where applicable). Health visible without drilling in.
 */

import { useEffect, useState } from 'react';
import {
  fetchCompanyStats,
  fetchCompanySettings,
  fetchUsers,
  fetchFtpTargets,
  fetchProfiles,
} from '../../../../api';
import { useAuth } from '../../../../auth.jsx';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Mono, StatusPill } from '../../../../shared/components/redesign';
import styles from './CompanyDashboard.module.css';

const navigateHash = (hash) => {
  window.location.hash = hash;
};

/**
 * Identity card pinned at the top of the page.
 */
function IdentityCard({ company, role }) {
  const initials = (company?.name || 'CO')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');
  return (
    <div className={styles.identityCard}>
      <span className={styles.identityAvatar}>{initials}</span>
      <div className={styles.identityText}>
        <Mono className={styles.identityEyebrow}>§ COMPANY</Mono>
        <h2 className={styles.identityName}>
          {company?.name || 'Your company'}
        </h2>
        <Mono className={styles.identityMeta}>
          {[company?.slug, role && `Role · ${role}`].filter(Boolean).join(' · ')}
        </Mono>
      </div>
    </div>
  );
}

/**
 * Section card with letter avatar + count + preview list.
 */
function SectionCard({
  letter,
  title,
  description,
  count,
  hash,
  preview,
  previewEmpty,
}) {
  return (
    <button
      type="button"
      className={styles.card}
      onClick={() => navigateHash(hash)}
    >
      <div className={styles.cardHead}>
        <span className={styles.cardAvatar}>{letter}</span>
        <div className={styles.cardHeadText}>
          <span className={styles.cardTitle}>{title}</span>
          <Mono className={styles.cardDesc}>{description}</Mono>
        </div>
        {count !== null && count !== undefined && (
          <span className={styles.cardCount}>{count}</span>
        )}
      </div>
      <div className={styles.cardPreview}>
        {preview && preview.length > 0 ? (
          <ul className={styles.previewList}>
            {preview.map((item, i) => (
              <li key={i} className={styles.previewItem}>
                {item.status && (
                  <span
                    className={`${styles.previewDot} ${styles[`dot-${item.status}`] || ''}`}
                    aria-hidden="true"
                  />
                )}
                <span className={styles.previewLabel}>{item.label}</span>
                {item.meta && (
                  <Mono className={styles.previewMeta}>{item.meta}</Mono>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.previewEmpty}>{previewEmpty}</p>
        )}
      </div>
    </button>
  );
}

export default function CompanyDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    stats: {},
    settings: null,
    users: [],
    ftpTargets: [],
    profiles: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [stats, settings, users, ftpTargets, profiles] = await Promise.all([
          fetchCompanyStats().catch(() => ({})),
          fetchCompanySettings().catch(() => null),
          fetchUsers({ limit: 5 }).catch(() => null),
          fetchFtpTargets({ limit: 5 }).catch(() => null),
          fetchProfiles().catch(() => null),
        ]);
        if (cancelled) return;
        setData({
          stats: stats || {},
          settings,
          users: users?.results || users || [],
          ftpTargets: ftpTargets?.results || ftpTargets || [],
          profiles: profiles?.results || profiles || [],
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading company settings…</p>
      </div>
    );
  }

  const userCount = data.stats.user_count ?? data.users.length;
  const ftpCount = data.stats.ftp_target_count ?? data.ftpTargets.length;
  const profileCount = data.stats.profile_count ?? data.profiles.length;

  // Build preview lists from the first 3 entries of each fetched list.
  const usersPreview = (data.users || []).slice(0, 3).map((u) => ({
    label: u.email || u.username || `User ${u.id}`,
    meta: u.role || null,
  }));
  const ftpPreview = (data.ftpTargets || []).slice(0, 3).map((t) => ({
    label: t.name || t.host || `FTP ${t.id}`,
    meta: t.host || null,
    status: t.is_active === false ? 'pending' : 'done',
  }));
  const profilesPreview = (data.profiles || []).slice(0, 3).map((p) => ({
    label: p.name || `Profile ${p.id}`,
    meta: p.is_default ? 'default' : null,
  }));

  return (
    <div className={styles.container}>
      <IdentityCard
        company={data.settings || user?.company}
        role={user?.role}
      />

      {/* Workspace group */}
      <section className={styles.group}>
        <Mono className={styles.groupHeader}>:: Workspace</Mono>
        <div className={styles.groupGrid}>
          <SectionCard
            letter="U"
            title="Users"
            description="Team members + roles"
            count={userCount}
            hash="company/users"
            preview={usersPreview}
            previewEmpty="No users yet."
          />
        </div>
      </section>

      {/* Output group */}
      <section className={styles.group}>
        <Mono className={styles.groupHeader}>:: Output</Mono>
        <div className={styles.groupGrid}>
          <SectionCard
            letter="T"
            title="FTP targets"
            description="Where processed photos ship"
            count={ftpCount}
            hash="company/ftp-targets"
            preview={ftpPreview}
            previewEmpty="No FTP targets configured."
          />
        </div>
      </section>

      {/* Captions group */}
      <section className={styles.group}>
        <Mono className={styles.groupHeader}>:: Captions</Mono>
        <div className={styles.groupGrid}>
          <SectionCard
            letter="P"
            title="Profiles"
            description="Image-processing presets"
            count={profileCount}
            hash="company/profiles"
            preview={profilesPreview}
            previewEmpty="No profiles yet."
          />
          <SectionCard
            letter="CB"
            title="Caption builder"
            description="Templates, connectors, grammar"
            count={null}
            hash="company/caption-builder"
            preview={null}
            previewEmpty="Open to configure."
          />
          <SectionCard
            letter="G"
            title="Settings"
            description="Company name + defaults"
            count={null}
            hash="company/settings"
            preview={null}
            previewEmpty="Open to edit."
          />
        </div>
      </section>
    </div>
  );
}
